import { OptionalFormController, OptionalFormInclude, required, useGridForm } from "@itk/react-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { Model_AttributeDetailsGet, Model_AttributeValueSet, Shared_DimensionParts } from "src/backend/openapi/v1";

type TAttributeValueChange = {
  attributeId: string;
  value: Model_AttributeValueSet[];
  shouldDebounce: boolean;
  onError: () => void;
};

// Shared by configure-product and the bulk editor; the caller decides what a change writes to.
export const useAttributeGridForm = (attributes: Model_AttributeDetailsGet[] | undefined, onAttributeValueChange: (change: TAttributeValueChange) => void, onAttributeChanged?: () => void) => {
  // What a save answers with is the truth about the field: the server can store the value as sent, blank
  // it (a List/Reference value with no resolvable option), clear it, or have a rule rewrite it. Nothing
  // local is held back to compete with that answer — every response re-seeds the form through initialData
  // below. A rejected save is the one case with no answer to seed from, so it bumps this counter instead;
  // the fresh array identity is what makes useGridForm rebuild and drop the input the server refused.
  const [failedEditTick, setFailedEditTick] = useState(0);

  // Copied rather than passed through: useGridForm re-seeds on a new array identity, which is how a
  // failed save gets its revert even though `attributes` itself hasn't changed.
  const initialData = useMemo(() => [...(attributes ?? [])], [attributes, failedEditTick]);

  const columns = useMemo(() => {
    if (attributes && attributes.length > 0) return Object.keys(attributes[0]).map(key => ({ field: key }));
    return [];
  }, [attributes]);

  const include = useMemo((): OptionalFormInclude<Model_AttributeDetailsGet> => {
    if (attributes && attributes.length > 0)
      return Object.keys(attributes[0]).reduce(
        (acc, x) => {
          const key = x as keyof Model_AttributeDetailsGet;
          acc[key] = true;
          return acc;
        },
        {} as Record<keyof Model_AttributeDetailsGet, true>
      );
    return {};
  }, [attributes]);

  const initController = useMemo(
    (): OptionalFormController<Model_AttributeDetailsGet> => ({
      value: {
        // Both apply: a required field can hold a value that's still rule-invalid
        validators: data => {
          const validators = [];
          if (data.isRequired.value) validators.push(required);
          if (!data.isValid.value) validators.push(() => "");
          return validators;
        }
      }
    }),
    []
  );

  const { rows, validate } = useGridForm<Model_AttributeDetailsGet, (typeof columns)[0], keyof Model_AttributeDetailsGet>(initialData, columns, "attributeId", initController, include);

  // useGridForm validates silently, so errors never reach form state — re-validate to render them
  const validateRef = useRef(validate);
  validateRef.current = validate;

  useEffect(() => {
    validateRef.current("all");
  }, [initialData]);

  // siblingValues carries the other parts of a DimensionType attribute (see attribute-renderer.tsx):
  // a dimension is written whole, so no request can report a completed dimension as half-filled.
  const handleChange = (
    attributeId: string | null,
    attributeValue: string | null,
    part: Shared_DimensionParts | null = null,
    shouldDebounce = false,
    siblingValues: Model_AttributeValueSet[] = []
  ) => {
    if (!attributeId) return;

    const value = attributeValue?.toString() ?? "";

    // Stringified for the same reason `value` above is: the Integer/Decimal fields are fed their state
    // through an `as unknown as FormFieldState<number>` cast (attribute-renderer.tsx), so a part the user
    // has touched holds a NUMBER at runtime however the type reads. Sent raw, it reaches the API as a JSON
    // number and the whole body fails to deserialize ("The JSON value could not be converted to
    // System.String"), taking the untouched sibling's save down with it.
    const values: Model_AttributeValueSet[] = [...siblingValues.map(sibling => ({ part: sibling.part ?? null, value: sibling.value?.toString() ?? "" })), { part, value }];

    onAttributeValueChange({
      attributeId,
      value: values,
      shouldDebounce,
      // The write failed, so every part it carried is unconfirmed — including a sibling whose own save was
      // coalesced into this one and will never arrive separately. Re-seeding restores all of them at once.
      onError: () => setFailedEditTick(tick => tick + 1)
    });

    onAttributeChanged?.();
  };

  return { rows, handleChange };
};
